/**
 * dateUtils.js — date/time formatting, store-hours, and range helpers (pure).
 */
import { STORE_HOURS } from '../../../constants';

export function localDateISO(d = new Date()) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export function tsToLocal(ts) {
  if (!ts) return null;
  if (ts.toDate) return ts.toDate();
  return new Date(ts);
}

export function fmtDateTime(d) {
  if (!d) return '—';
  return d.toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

export function fmtMinutes(n) {
  if (n == null) return '—';
  if (n < 1) return '< 1 min';
  if (n < 60) return `${Math.round(n)} min`;
  return `${Math.floor(n / 60)}h ${Math.round(n % 60)}m`;
}

export function elapsedMin(createdAt, now = new Date()) {
  if (!createdAt) return 0;
  const d = createdAt instanceof Date ? createdAt : new Date(createdAt);
  return (now.getTime() - d.getTime()) / 60000;
}

export function fmtElapsed(mins) {
  if (mins < 1) return '< 1m';
  if (mins < 60) return `${Math.floor(mins)}m`;
  return `${Math.floor(mins / 60)}h ${Math.floor(mins % 60)}m`;
}

export function shortDate(iso) {
  const [, m, d] = iso.split('-');
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${months[parseInt(m) - 1]} ${parseInt(d)}`;
}

export function toLocalInput(d) {
  if (!d) return '';
  const pad = n => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function isStoreOpen(now = new Date()) {
  const day = now.getDay();
  const slots = STORE_HOURS[day] || [];
  const mins = now.getHours() * 60 + now.getMinutes();
  return slots.some(({ open, close }) => {
    const [oh, om] = open.split(':').map(Number);
    const [ch, cm] = close.split(':').map(Number);
    return mins >= oh * 60 + om && mins < ch * 60 + cm;
  });
}

export function computeRange(preset, customStart, customEnd) {
  const now = new Date();
  if (preset === 'today') {
    const s = new Date(now); s.setHours(0, 0, 0, 0);
    const e = new Date(now); e.setHours(23, 59, 59, 999);
    return { startDt: s, endDt: e };
  }
  if (preset === 'yesterday') {
    const s = new Date(now); s.setDate(s.getDate() - 1); s.setHours(0, 0, 0, 0);
    const e = new Date(s); e.setHours(23, 59, 59, 999);
    return { startDt: s, endDt: e };
  }
  // 7d / 30d end at the END of today, not "now". A range ending at `now` gave the
  // Firestore query a new shape on every mount, so it could never resume from the
  // local cache and re-read up to its full limit (1–2k docs) each time a tab
  // opened. End-of-today keeps the query identical all day; it selects the same
  // tickets, since nothing is updated in the future.
  if (preset === '7d') {
    const s = new Date(now); s.setDate(s.getDate() - 6); s.setHours(0, 0, 0, 0);
    const e = new Date(now); e.setHours(23, 59, 59, 999);
    return { startDt: s, endDt: e };
  }
  if (preset === '30d') {
    const s = new Date(now); s.setDate(s.getDate() - 29); s.setHours(0, 0, 0, 0);
    const e = new Date(now); e.setHours(23, 59, 59, 999);
    return { startDt: s, endDt: e };
  }
  if (preset === 'custom' && customStart && customEnd) {
    return { startDt: new Date(customStart), endDt: new Date(customEnd) };
  }
  const s = new Date(now); s.setHours(0, 0, 0, 0);
  const e = new Date(now); e.setHours(23, 59, 59, 999);
  return { startDt: s, endDt: e };
}
